import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, TextField, Alert,
  Skeleton, InputAdornment, Avatar, Select, MenuItem, FormControl,
  InputLabel, Tab, Tabs,
} from '@mui/material';
import {
  SearchOutlined, AssignmentOutlined, ArrowBackOutlined,
  EditOutlined, ListOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../hooks/useApi';
import { StatusChip, FORM_STATUS, fmtDate, fmtDateTime } from '../components/DashboardShared';

const PRIMARY = '#1075BA';

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Form editor placeholder ───────────────────────────────────────────────────
function FormEditorTab({ form }) {
  return (
    <Card sx={{ borderRadius: 3 }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${alpha(PRIMARY, 0.08)}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Form Editor</Typography>
        <Typography variant="caption" color="text.secondary">Visual form builder — coming soon</Typography>
      </Box>
      <Box sx={{ p: 3 }}>
        {/* Form metadata */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Name', value: form.name },
            { label: 'Slug', value: `/${form.slug}` },
            { label: 'Type', value: form.formType || 'registration' },
            { label: 'Steps', value: form.steps?.length ?? 0 },
          ].map(item => (
            <Box key={item.label} sx={{ background: alpha(PRIMARY, 0.04), border: `1px solid ${alpha(PRIMARY, 0.1)}`, borderRadius: 2, px: 2, py: 1.5, minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem', fontWeight: 700 }}>{item.label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25, textTransform: 'capitalize' }}>{item.value}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ background: alpha(PRIMARY, 0.025), border: `1px dashed ${alpha(PRIMARY, 0.25)}`, borderRadius: 3, p: 5, textAlign: 'center' }}>
          <EditOutlined sx={{ fontSize: 40, color: alpha(PRIMARY, 0.3), mb: 1.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Form builder UI</Typography>
          <Typography variant="caption" color="text.secondary">Drag-and-drop field editor will render here</Typography>
        </Box>

        {/* Steps preview */}
        {form.steps?.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 1.5 }}>Steps ({form.steps.length})</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {form.steps.map((step, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, background: '#fff', border: `1px solid ${alpha(PRIMARY, 0.1)}`, borderRadius: 2 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: alpha(PRIMARY, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: PRIMARY }}>{i + 1}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{step.title || `Step ${i + 1}`}</Typography>
                    <Typography variant="caption" color="text.secondary">Type: {step.type || 'fields'} · {step.sections?.length || 0} section(s)</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
}

// ── Submissions tab ───────────────────────────────────────────────────────────
function SubmissionsTab({ form }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    if (!form?._id) return;
    setLoading(true);
    api.get(`/submissions?formId=${form._id}`)
      .then(r => setSubmissions(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [form?._id]);

  const STATUS_META = {
    pending:   { color: '#e07b00' },
    approved:  { color: '#00a882' },
    rejected:  { color: '#d32f2f' },
    cancelled: { color: '#888' },
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${alpha(PRIMARY, 0.08)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Submissions</Typography>
          <Typography variant="caption" color="text.secondary">{loading ? 'Loading…' : `${submissions.length} submission${submissions.length !== 1 ? 's' : ''}`}</Typography>
        </Box>
        <Button size="small" variant="outlined" sx={{ borderRadius: 2 }}>Export CSV</Button>
      </Box>
      {loading ? (
        <Box sx={{ p: 2.5 }}>{[...Array(4)].map((_, i) => <Box key={i} sx={{ display: 'flex', gap: 2, py: 1.5 }}><Box sx={{ flex: 1 }}><Skeleton width="40%" /><Skeleton width="25%" height={14} /></Box><Skeleton width={80} height={22} /></Box>)}</Box>
      ) : submissions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 7 }}>
          <ListOutlined sx={{ fontSize: 44, color: alpha(PRIMARY, 0.2), mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary">No submissions yet.</Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Submitter</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map(sub => {
                const s = STATUS_META[sub.status] || STATUS_META.pending;
                return (
                  <TableRow key={sub._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{sub.submitterName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{sub.submitterEmail || '—'}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{fmtDateTime(sub.createdAt)}</Typography></TableCell>
                    <TableCell>
                      <Chip label={sub.status} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize', background: alpha(s.color, 0.09), color: s.color, border: `1px solid ${alpha(s.color, 0.22)}` }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
                        {sub.referenceNumber || sub._id?.slice(-8).toUpperCase()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

// ── Form detail ───────────────────────────────────────────────────────────────
function FormDetail({ formId }) {
  const navigate          = useNavigate();
  const [form, setForm]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab]     = useState(0);

  useEffect(() => {
    setLoading(true); setError('');
    api.get(`/forms/${formId}`)
      .then(r => setForm(r.data.data))
      .catch(e => setError(e?.response?.data?.message || 'Failed to load form'))
      .finally(() => setLoading(false));
  }, [formId]);

  if (loading) return <Box sx={{ p: 3 }}><Skeleton height={40} sx={{ mb: 2 }} /><Skeleton height={200} /></Box>;
  if (error)   return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  if (!form)   return null;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackOutlined />} onClick={() => navigate('/dashboard/forms')}
          sx={{ color: 'text.secondary', fontWeight: 600, mb: 1.5, pl: 0, '&:hover': { background: 'transparent', color: PRIMARY } }}>
          All Forms
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800 }}>{form.name}</Typography>
          <StatusChip status={form.status} map={FORM_STATUS} />
          <Chip label={form.formType || 'registration'} size="small" sx={{ fontSize: '0.7rem', height: 22, textTransform: 'capitalize', background: alpha(PRIMARY, 0.07), color: PRIMARY }} />
        </Box>
        {form.event && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Event: <strong>{form.event.name}</strong>
          </Typography>
        )}
      </Box>

      <Box sx={{ borderBottom: `1px solid ${alpha(PRIMARY, 0.1)}` }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ '& .MuiTabs-indicator': { background: PRIMARY, height: 2.5 }, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary', minHeight: 44, '&.Mui-selected': { color: PRIMARY } } }}>
          <Tab label="Form Editor" icon={<EditOutlined sx={{ fontSize: 16 }} />} iconPosition="start" />
          <Tab label={`Submissions ${form.submissionCount ? `(${form.submissionCount})` : ''}`} icon={<ListOutlined sx={{ fontSize: 16 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}><FormEditorTab form={form} /></TabPanel>
      <TabPanel value={tab} index={1}><SubmissionsTab form={form} /></TabPanel>
    </Box>
  );
}

// ── Forms list ────────────────────────────────────────────────────────────────
function FormsList() {
  const navigate          = useNavigate();
  const [forms, setForms] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.get('/forms'), api.get('/events')])
      .then(([fRes, eRes]) => { setForms(fRes.data.data || []); setEvents(eRes.data.data || []); })
      .catch(e => setError(e?.response?.data?.message || 'Failed'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = forms.filter(f => {
    const ms = !search || f.name.toLowerCase().includes(search.toLowerCase());
    const me = !eventFilter || f.event?._id === eventFilter || f.event === eventFilter;
    return ms && me;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 800 }}>Forms</Typography>
          <Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : `${forms.length} form${forms.length !== 1 ? 's' : ''}`}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search forms…" size="small" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by event</InputLabel>
            <Select value={eventFilter} label="Filter by event" onChange={e => setEventFilter(e.target.value)}>
              <MenuItem value="">All events</MenuItem>
              {events.map(ev => <MenuItem key={ev._id} value={ev._id}>{ev.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <Card sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 2.5 }}>{[...Array(5)].map((_, i) => <Box key={i} sx={{ display: 'flex', gap: 2, py: 1.5 }}><Box sx={{ flex: 1 }}><Skeleton width="40%" /><Skeleton width="25%" height={14} /></Box><Skeleton width={70} height={22} /><Skeleton width={80} height={22} /></Box>)}</Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 7 }}>
            <AssignmentOutlined sx={{ fontSize: 44, color: alpha(PRIMARY, 0.22), mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary">{search || eventFilter ? 'No forms match.' : 'No forms yet.'}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Form Name</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Submissions</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(f => (
                  <TableRow key={f._id} hover onClick={() => navigate(`/dashboard/forms/${f._id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { background: alpha(PRIMARY, 0.025) } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar variant="rounded" sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: alpha(PRIMARY, 0.1), color: PRIMARY, fontSize: '0.75rem', fontWeight: 700 }}>
                          {f.name?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.name}</Typography>
                          <Typography variant="caption" color="text.secondary">/{f.slug}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{f.event?.name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={f.formType || 'registration'} size="small" sx={{ fontSize: '0.7rem', height: 20, textTransform: 'capitalize', background: alpha(PRIMARY, 0.07), color: PRIMARY }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.submissionCount ?? 0}</Typography>
                    </TableCell>
                    <TableCell><StatusChip status={f.status} map={FORM_STATUS} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}

export default function FormsPage() {
  const { id } = useParams();
  return id ? <FormDetail formId={id} /> : <FormsList />;
}